import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FullAddressModel } from '../model/common/full-address.model';
import { LoadScriptService } from '../services/load-script.service';

@Directive({
  selector: '[appFullAddress]',
  standalone: false
})
export class FullAddressDirective {
  @Input() country: string = null;
  @Output() addressChange = new EventEmitter<FullAddressModel[]>();

  private readonly defaultCountries = ['ca', 'us'];

  constructor(private elRef: ElementRef<HTMLInputElement>, private loadScriptService: LoadScriptService) {
    this.loadScriptService.loadScript(LoadScriptService.GOOGLE_ADDRESS).then(() => {
    });
  }

  // Input event — user typing
  @HostListener('input', ['$event.target.value'])
  onInput(value: string) {
    this.handleInput(value);
  }

  // Focus event — user focuses the input (e.g., clicks or tabs in)
  @HostListener('focus')
  onFocus(): void {
    const value = this.elRef.nativeElement.value;
    this.handleInput(value);
  }

  // Click event — user clicks inside input
  @HostListener('click')
  onClick(): void {
    const value = this.elRef.nativeElement.value;
    this.handleInput(value);
  }

  // Central handler for suggestions, etc.
  async handleInput(value: string): Promise<void> {
    let listOfFullAddress = [];
    this.addressChange.emit(listOfFullAddress);
    if (!value || (value?.trim()?.length <= 0)) {
      return;
    }

    try {
      //@ts-ignore
      const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

      let request = {
        input: value,
        sessionToken: '',
        includedRegionCodes: (this.country && this.country.trim().length) ? [this.country.toLowerCase()] : this.defaultCountries
      };

      // Create a session token.
      // @ts-ignore
      const token = new AutocompleteSessionToken();
      // @ts-ignore
      request.sessionToken = token;

      // Fetch autocomplete suggestions.
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (suggestions?.length) {
        const addressPromises = suggestions.map(async suggestion => {
          const placePrediction = suggestion.placePrediction;
          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: ['displayName', 'formattedAddress', 'addressComponents'],
          });

          const address = new FullAddressModel();
          address.displayName = placePrediction.text.toString();

          if (place) {
            address.address = place.displayName;
            address.fullAddress = place.formattedAddress;
            place.addressComponents.forEach((value: google.maps.places.AddressComponent) => {
              if (value.types.indexOf('postal_code') !== -1) {
                address.postalCode = value.longText ?? '';
              }
              if (value.types.indexOf('locality') !== -1) {
                address.city = value.longText ?? '';
              }
              if (value.types.indexOf('administrative_area_level_1') !== -1) {
                address.jurisdiction = value.longText ?? '';
                address.jurisdictionCode = value.shortText ?? '';
              }
              if (value.types.indexOf('country') !== -1) {
                address.country = value.longText ?? '';
                address.countryCode = value.shortText ?? '';
              }
            });
          }
          return address; // Return the populated address model
        });

        listOfFullAddress = await Promise.all(addressPromises);
        this.addressChange.emit(listOfFullAddress);
      } else {
        // If no suggestions, emit an empty array (already done at the start, but good to be explicit)
        this.addressChange.emit([]);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      this.addressChange.emit([]); // Emit empty on error
    }
  }
}
